'use client';

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import * as React from 'react';

import { FarIcons } from '@playbooks/icons';
import { Button } from '@playbooks/ui/components/button';
import { backdropVariants, panelElevationVariants } from '@playbooks/ui/components/component-recipes';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';
import { cn } from '@playbooks/ui/lib/utils';

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
	return <SheetPrimitive.Root data-slot='sheet' {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
	return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
	return <SheetPrimitive.Close data-slot='sheet-close' {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
	return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />;
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
	// Theme
	const rootClassName = cn(backdropVariants({ strength: 'default', motion: 'standard' }), className);

	// Render
	return <SheetPrimitive.Backdrop data-slot='sheet-overlay' className={rootClassName} {...props} />;
}

function SheetContent({
	className,
	children,
	side = 'right',
	showCloseButton = true,
	closeButtonClassName,
	...props
}: SheetPrimitive.Popup.Props & {
	side?: 'top' | 'right' | 'bottom' | 'left';
	showCloseButton?: boolean;
	closeButtonClassName?: string;
}) {
	// Theme
	const rootClassName = cn(
		panelElevationVariants({ elevation: 'overlay', surface: 'raised' }),
		'fixed z-overlay flex flex-col gap-4 bg-clip-padding text-sm transition duration-standard ease-in-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem] sm:data-[side=left]:max-w-sm sm:data-[side=right]:max-w-sm',
		className,
	);

	// Render
	return (
		<SheetPortal>
			<SheetOverlay />
			<SheetPrimitive.Popup data-slot='sheet-content' data-side={side} className={rootClassName} {...props}>
				{children}
				{showCloseButton && (
					<SheetPrimitive.Close
						data-slot='sheet-close'
						render={
							<Button variant='ghost' className={cn('absolute top-3 right-3', closeButtonClassName)} size='icon-sm' />
						}>
						<StructuralIcon icon={FarIcons.farXmark} />
						<span className='sr-only'>Close</span>
					</SheetPrimitive.Close>
				)}
			</SheetPrimitive.Popup>
		</SheetPortal>
	);
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('flex flex-col gap-0.5 p-4', className);

	// Render
	return <div data-slot='sheet-header' className={rootClassName} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
	// Theme
	const rootClassName = cn('mt-auto flex flex-col gap-2 p-4', className);

	// Render
	return <div data-slot='sheet-footer' className={rootClassName} {...props} />;
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
	// Theme
	const rootClassName = cn('text-base font-medium text-foreground', className);

	// Render
	return <SheetPrimitive.Title data-slot='sheet-title' className={rootClassName} {...props} />;
}

function SheetDescription({ className, ...props }: SheetPrimitive.Description.Props) {
	// Theme
	const rootClassName = cn('text-sm text-foreground-body', className);

	// Render
	return <SheetPrimitive.Description data-slot='sheet-description' className={rootClassName} {...props} />;
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
